import { Inject, Injectable, PLATFORM_ID, NgZone } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpClient, HttpRequest, HttpResponse, HttpEvent, HttpEventType, HttpParams, HttpHeaders } from '@angular/common/http';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { GlobalComponentService } from '@techmore/global';
import { Observable, throwError, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as memoryCache from 'memory-cache';
import { printErrorHandler } from 'src/app/util';
import { LoggerService } from './logger.service';
import { CSP_NONCE_KEY } from '../tokens/csp-nonce.statekey';
import { GENERATOR_ENV_TOKEN, GeneratorEnv } from '../../web-generator/generator-env-token';


@Injectable({
  providedIn: 'root'
})
export class CommonHttpClientService {
  /** 是瀏覽器端渲染 */
  isBrowser: boolean;
  public words: any = {};

  constructor(
    private http: HttpClient,
    private globalComponentService: GlobalComponentService,
    private translateService: TranslateService,
    @Inject(PLATFORM_ID) protected platformId: object,
    private transferState: TransferState,
    private ngZone: NgZone,
    private loggerService: LoggerService,
    @Inject(GENERATOR_ENV_TOKEN) private generatorEnv: GeneratorEnv,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.translateService.get('modal.errorTitle').subscribe(s => this.words.errorTitle = s);
    this.translateService.get('error.addressNotFound').subscribe(s => this.words.addressNotFound = s);
    this.translateService.get('error.undefinedError').subscribe(s => this.words.undefinedError = s);
  }

  get<T>(url: string, options?: any, noDefaultPrintAction = false) {
    const httpOptions = Object.assign({responseType: 'json'}, options);
    if (httpOptions['params']) {
      httpOptions['params'] = new HttpParams({fromObject: httpOptions['params']});
    }
    if (httpOptions['headers']) {
      httpOptions['headers'] = new HttpHeaders(httpOptions['headers']);
    }
    const req = new HttpRequest('GET', url, httpOptions);
    return this.onHttpRequest<T>(req).pipe(
      catchError((error, caught) => {
        if (!noDefaultPrintAction) {
          return printErrorHandler(error, url, this.translateService, this.globalComponentService, this.words, this.isBrowser, this.loggerService);
        } else {
          return throwError(error);
        }
      }),
    );

    // return this.http.get<HttpResponse<T>>(`${url}`, options).pipe(
    //   catchError((error, caught) => {
    //     if (!noDefaultPrintAction) {
    //       return printErrorHandler(error, url, this.translateService, this.globalComponentService, this.words, this.isBrowser);
    //     } else {
    //       return throwError(error);
    //     }
    //   }),
    // ) as Observable<unknown> as Observable<T>;
  }

  post<T>(url: string, data: any, options?: any, noDefaultPrintAction = false) {
    return this.http.post<T>(`${url}`, data, options).pipe(
      catchError((error, caught) => {
        if (!noDefaultPrintAction) {
          return printErrorHandler(error, url, this.translateService, this.globalComponentService, this.words, this.isBrowser, this.loggerService);
        } else {
          return throwError(error);
        }
      })
    ) as Observable<any>;
  }

  put<T>(url: string, data: any, options?: any, noDefaultPrintAction = false) {
    return this.http.put<T>(`${url}`, data, options).pipe(
      catchError((error, caught) => {
        if (!noDefaultPrintAction) {
          return printErrorHandler(error, url, this.translateService, this.globalComponentService, this.words, this.isBrowser, this.loggerService);
        } else {
          return throwError(error);
        }
      })
    ) as Observable<any>;
  }

  delete<T>(url: string, options?: any, noDefaultPrintAction = false) {
    return this.http.delete<T>(`${url}`, options).pipe(
      catchError((error, caught) => {
        if (!noDefaultPrintAction) {
          return printErrorHandler(error, url, this.translateService, this.globalComponentService, this.words, this.isBrowser, this.loggerService);
        } else {
          return throwError(error);
        }
      })
    ) as Observable<any>;
  }

  private onHttpRequest<T>(req: HttpRequest<T>): Observable<T> {
    const isServer = isPlatformServer(this.platformId);
    const isSSRMode = isServer || this.transferState.hasKey(CSP_NONCE_KEY);
    const key = `${req.method}.${req.urlWithParams.replace(this.generatorEnv.apiHost, '')}`;

    if (isSSRMode) {
      if (isServer) {
        this.loggerService.info(`HTTP request: ${key}`);
        // server side 處理 api http 請求
        const cachedData = memoryCache.get(key) as HttpResponse<T>;
        if (cachedData) {
          this.transferState.set<HttpResponse<T>>(makeStateKey(key), cachedData);
          this.loggerService.info(`HTTP response from MemoryCache: ${key}`);
          return of(cachedData.body);
        }
      } else {
        // broswer side 處理 api http 請求
        // if (req.method !== 'GET') {
        //   return next.handle(req);
        // }
        const storedResponse: HttpResponse<T> = this.transferState.get(makeStateKey(key), null);
        if (storedResponse) {
          this.loggerService.info(`HTTP request: ${key}`);
          const response = storedResponse;
          this.loggerService.info(`HTTP response: ${key}\n`, response);
          return of(response.body);
        }
      }
    }

    return new Observable<T>(observer => {
      this.http.request<T>(req).subscribe((event: HttpEvent<T>) => {
        switch (event.type) {
          case HttpEventType.Sent:
            break;
          case HttpEventType.UploadProgress:
            break;
          case HttpEventType.Response:
            if (isSSRMode && isServer) {
              // if (event instanceof HttpResponse) {
              this.transferState.set<any>(makeStateKey(key), event);
              // console.log(`${moment().format('YYYY/MM/DD')} HTTP response: ${key}\n`, JSON.stringify(event));
              // this.ngZone.runOutsideAngular(() => {
              //   memoryCache.put(key, event, 1000 * 60);
              // });
              // }
            }
            observer.next(event.body);
            observer.complete();
        }
      }, error => {
        observer.error(error);
        observer.complete();
      });
    });
  }
}
